import React from 'react';
import { Video, X, Loader2, Eye, Heart, Clock, Edit2 } from 'lucide-react';
import { FeaturedSubmit } from './FeaturedSubmit';
import { formatWatchTime } from './studioShared';
import { HookEditor } from './HookEditor';

// Studio "My Videos" grid with per-video actions (extracted from StudioPage).
export const StudioVideoGrid = ({
  axiosInstance,
  disclosure,
  fetchMyVideos,
  getThumbnail,
  handleDelete,
  handleReconcile,
  playVideo,
  reconcilingId,
  setEditDetailsVideo,
  setRetagVideo,
  videos,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
    {videos.map((video, index) => (
      <div key={video.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        {/* Video thumbnail */}
        <div 
          className="aspect-video bg-zinc-800 cursor-pointer relative group"
          onClick={() => video.status === 'processing' ? null : playVideo(video, videos, index)}
        >
          <img
            src={getThumbnail(video, index)}
            alt={video.title}
            className="w-full h-full object-cover"
          />
          {(() => {
            // ── Overlay decision (iter 113, front-end display-only) ──
            // The failure overlay's job is to warn creators that
            // their upload never made it to our video CDN. That's
            // the ONLY case where re-uploading is the right action.
            //
            // If Bunny has an asset for this row (`bunny_video_guid`
            // or `hls_url` present), the upload succeeded — even
            // when our DB row is momentarily `failed` from an
            // out-of-order status webhook or an inline post-upload
            // check that fired before Bunny finished registering
            // the bytes. Showing UPLOAD FAILED for those is a
            // false alarm that scares creators into re-uploading
            // a video that's about to play just fine.
            //
            // Rules (see PRD note iter 113):
            //  • status === 'processing' → orange PROCESSING. Never
            //    show FAILED here even if the row also carries a
            //    stale processing_error from a previous cycle.
            //  • Bunny has an asset (guid || hls_url) AND row is
            //    not yet playable (no hls_url yet) → orange
            //    PROCESSING. Bunny is transcoding.
            //  • Bunny has an asset AND hls_url is set → no
            //    overlay. Normal playable card + hover-play.
            //  • status === 'failed' AND no Bunny asset at all →
            //    the only case that shows red UPLOAD FAILED.
            //  • Anything else → no overlay (approved / pending
            //    review chip is rendered separately below).
            //
            // This is purely a display guard — we do NOT touch
            // status, bunny_video_guid, hls_url, the webhook, the
            // reconciler, or the upload flow.
            const hasBunnyAsset = Boolean(video.bunny_video_guid || video.hls_url);
            const isPlayable = Boolean(video.hls_url);

            if (video.status === 'processing') {
              return (
                <div
                  className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2"
                  data-testid={`video-status-processing-${video.id}`}
                >
                  <Loader2 className="w-7 h-7 text-orange-400 animate-spin" />
                  <span className="text-xs font-medium text-orange-200 tracking-wide">PROCESSING</span>
                  <span className="text-[10px] text-zinc-400 px-3 text-center">Processing. This might take a bit.</span>
                  <span className="text-[9px] text-zinc-500 px-3 text-center leading-snug">
                    Stuck for a while? Email{' '}
                    <a
                      href="mailto:courtney@audivue.io"
                      className="underline underline-offset-2 hover:text-zinc-300"
                      data-testid={`processing-help-mailto-${video.id}`}
                    >
                      courtney@audivue.io
                    </a>
                    {' '}and I&apos;ll sort it out.
                  </span>
                </div>
              );
            }

            if (hasBunnyAsset && !isPlayable) {
              // Bunny has bytes but no playable manifest yet — it's
              // transcoding. Show the same PROCESSING overlay so the
              // creator sees a benign state instead of the red FAILED
              // one, even if our DB says status === 'failed'.
              return (
                <div
                  className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2"
                  data-testid={`video-status-processing-bunny-${video.id}`}
                >
                  <Loader2 className="w-7 h-7 text-orange-400 animate-spin" />
                  <span className="text-xs font-medium text-orange-200 tracking-wide">PROCESSING</span>
                  <span className="text-[10px] text-zinc-400 px-3 text-center">Video service is finishing up — this may take a moment.</span>
                </div>
              );
            }

            if (video.status === 'failed' && !hasBunnyAsset) {
              // Real failure — nothing reached Bunny. Show the
              // recovery UI so the creator can either link a
              // lost row back to a Bunny asset or re-upload.
              return (
                <div
                  className="absolute inset-0 bg-red-950/80 flex flex-col items-center justify-center gap-2 p-3 text-center"
                  data-testid={`video-status-failed-${video.id}`}
                >
                  <X className="w-7 h-7 text-red-400" />
                  <span className="text-xs font-medium text-red-200 tracking-wide">UPLOAD FAILED</span>
                  <span className="text-[10px] text-red-200/80 leading-tight">
                    {video.processing_error || 'Please try uploading again.'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleReconcile(video); }}
                    disabled={reconcilingId === video.id}
                    className="mt-1 px-3 py-1 rounded-md bg-red-500/30 hover:bg-red-500/50 disabled:opacity-50 border border-red-400/40 text-[11px] font-medium text-red-100 transition-colors"
                    data-testid={`video-retry-btn-${video.id}`}
                  >
                    {reconcilingId === video.id ? 'Checking…' : 'Try to recover'}
                  </button>
                </div>
              );
            }

            return null;
          })()}
          {video.status === 'pending_review' && (
            <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-yellow-500/90 text-black text-[10px] font-bold tracking-wide">
              PENDING REVIEW
            </div>
          )}
          {/* Hover-play indicator: show whenever the row is playable
              (Bunny has produced hls_url). Previously gated on
              status !== failed which suppressed the play affordance
              for videos that Bunny successfully transcoded even
              though our row was still marked failed by a stale
              webhook. Gating on hls_url is the correct signal. */}
          {Boolean(video.hls_url) && video.status !== 'processing' && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                <Video className="w-6 h-6 text-black" />
              </div>
            </div>
          )}
        </div>

        {/* Video info */}
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-white truncate">{video.title}</h3>
              <p className="text-sm text-zinc-500">{video.artist}</p>
            </div>
            <button
              onClick={() => handleDelete(video.id)}
              className="text-zinc-500 hover:text-red-400 p-1 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 text-xs">
            {video.genre}
          </span>
        </div>

        {/* Analytics */}
        <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/30">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-400">
                <Eye className="w-4 h-4" />
                <span className="text-lg font-bold text-white">{video.views || 0}</span>
              </div>
              <p className="text-xs text-zinc-500">Views</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-400">
                <Heart className="w-4 h-4" />
                <span className="text-lg font-bold text-white">{video.likes || 0}</span>
              </div>
              <p className="text-xs text-zinc-500">Likes</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-400">
                <Clock className="w-4 h-4" />
                <span className="text-lg font-bold text-white">{formatWatchTime(video.watch_time_seconds)}</span>
              </div>
              <p className="text-xs text-zinc-500">Watch Time</p>
            </div>
          </div>
        </div>

        {/* Hook editor — Pulse plays this 20-40s window as the trailer */}
        <HookEditor video={video} axiosInstance={axiosInstance} onSaved={fetchMyVideos} />

        {/* Featured submission CTA — manual quality gate */}
        <FeaturedSubmit video={video} axiosInstance={axiosInstance} onUpdated={fetchMyVideos} />

        {/* Quick AI disclosure retag button */}
        <div className="px-4 py-2 border-t border-zinc-800 flex items-center justify-between gap-2">
          <div className="text-[11px] text-zinc-500 truncate">
            {video.ai_disclosure?.needs_disclosure ? (
              <span className="text-amber-400">⚠ AI disclosure missing</span>
            ) : (
              <span>AI disclosure on file</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setEditDetailsVideo(video)}
              className="flex items-center gap-1 text-[11px] text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2"
              data-testid={`edit-details-btn-${video.id}`}
            >
              <Edit2 className="w-3 h-3" />
              Edit details
            </button>
            <button
              onClick={() => setRetagVideo(video)}
              className="text-[11px] text-white hover:text-zinc-300 underline underline-offset-2"
              data-testid={`retag-btn-${video.id}`}
            >
              Edit AI tags
            </button>
          </div>
        </div>
      </div>
    ))}
  </div>
);
